const dateFormatter = new Intl.DateTimeFormat("el-GR", {
  timeZone: "Europe/Athens",
});

const asText = (value) => value?.toString?.().trim?.() ?? "";

const formatDate = (value) => {
  if (!value) return "";

  const date = value instanceof Date ? value : new Date(value);
  return Number.isNaN(date.getTime()) ? asText(value) : dateFormatter.format(date);
};

const isVisible = (mode) => asText(mode).toLowerCase() !== "hidden";
const REVIEW_SUMMARY_SECTION_HEADINGS = new Set([
  "ΑΙΤΙΟΛΟΓΙΑ ΠΑΡΑΣΤΑΤΙΚΟΥ",
  "ΓΕΝΙΚΟ ΣΧΟΛΙΟ ΕΛΕΓΧΟΥ",
  "ΑΝΑΛΥΣΗ ΔΑΠΑΝΩΝ",
  "ΠΛΗΡΩΜΕΣ",
]);

const escapeHtml = (value) =>
  value
    .replaceAll("&", "&amp;")
    .replaceAll("<", "&lt;")
    .replaceAll(">", "&gt;")
    .replaceAll('"', "&quot;")
    .replaceAll("'", "&#39;");

const formatHtmlText = (value) =>
  escapeHtml(value).replace(/\r?\n/g, "<br />");

export const getReviewCommentText = (row) =>
  asText(
    row?.statusComments ??
      row?.reviewComments ??
      row?.expStatusComments ??
      row?.ExpStatusComments ??
      row?.ReviewComments,
  );

const addField = (fields, label, value) => {
  const text = asText(value);
  if (text) fields.push(`${label}: ${text}`);
};

const getExpenseRowHeading = (row, index, modeConfig) => {
  const fields = [];

  if (isVisible(modeConfig.employeeMode)) {
    addField(fields, "Εργαζόμενος", row?.employeeFullName);
  }
  if (isVisible(modeConfig.childMode)) {
    addField(fields, "Ωφελούμενος", row?.childFullName);
  }
  if (isVisible(modeConfig.appartmentMode)) {
    addField(fields, "Διαμέρισμα", row?.appartmentName);
  }
  addField(fields, "Είδος Δαπάνης", row?.expTypeName);
  if (isVisible(modeConfig.dateFromMode)) {
    addField(fields, "Από", formatDate(row?.dateFrom));
  }
  if (isVisible(modeConfig.dateToMode)) {
    addField(fields, "Έως", formatDate(row?.dateTo));
  }

  return `${index + 1}. ${fields.join(" · ") || "Γραμμή δαπάνης"}`;
};

const getPaymentHeading = (payment, index) => {
  const number = asText(payment?.paymentNumber) || index + 1;
  const date = asText(payment?.paymentDateFormatted) || formatDate(payment?.paymentDate);
  const fields = [`Πληρωμή ${number}`];
  addField(fields, "Ημ/νία Πληρωμής", date);

  return `${index + 1}. ${fields.join(" · ")}`;
};

const formatReviewRows = (rows, getHeading) => {
  const lines = rows.flatMap((row, index) => {
    const comment = getReviewCommentText(row);
    return comment
      ? [getHeading(row, index), `Σχόλιο ελέγχου: ${comment}`]
      : [];
  });

  return lines.length ? lines : ["Δεν υπάρχουν σχόλια ελέγχου."];
};

const buildReviewClipboardSummaryLines = ({
  transaction,
  mhdName,
  modeConfig = {},
  expenseRows = [],
  payments = [],
  reviewComments,
}) => {
  const title = [
    asText(transaction?.mhdName) || asText(mhdName) || asText(transaction?.mhdAbbr),
    asText(transaction?.transTypeName),
    asText(transaction?.transDateFormatted) || formatDate(transaction?.transDate),
  ]
    .filter(Boolean)
    .join(" - ");
  const generalReviewComment = asText(
    reviewComments ??
      transaction?.reviewComments ??
      transaction?.statusComments ??
      transaction?.expStatusComments,
  );
  const transactionReason = asText(transaction?.description);

  return [
    title,
    "",
    ...(transactionReason
      ? ["ΑΙΤΙΟΛΟΓΙΑ ΠΑΡΑΣΤΑΤΙΚΟΥ", transactionReason, ""]
      : []),
    ...(generalReviewComment
      ? ["ΓΕΝΙΚΟ ΣΧΟΛΙΟ ΕΛΕΓΧΟΥ", generalReviewComment, ""]
      : []),
    "ΑΝΑΛΥΣΗ ΔΑΠΑΝΩΝ",
    ...(expenseRows.length
      ? formatReviewRows(expenseRows, (row, index) =>
          getExpenseRowHeading(row, index, modeConfig),
        )
      : ["Δεν υπάρχουν γραμμές δαπάνης."]),
    "",
    "ΠΛΗΡΩΜΕΣ",
    ...(payments.length
      ? formatReviewRows(payments, getPaymentHeading)
      : ["Δεν υπάρχουν πληρωμές."]),
  ];
};

export const buildReviewClipboardSummary = (options) =>
  buildReviewClipboardSummaryLines(options).join("\n");

export const buildReviewClipboardSummaryHtml = (options) => {
  const lines = buildReviewClipboardSummaryLines(options);

  return [
    "<div>",
    ...lines.map((line, index) => {
      if (!line) return "<br />";
      if (index === 0) return `<p><strong>${formatHtmlText(line)}</strong></p>`;
      if (REVIEW_SUMMARY_SECTION_HEADINGS.has(line)) {
        return `<h3>${formatHtmlText(line)}</h3>`;
      }

      const reviewCommentPrefix = "Σχόλιο ελέγχου: ";
      if (line.startsWith(reviewCommentPrefix)) {
        return `<p><strong>Σχόλιο ελέγχου:</strong> ${formatHtmlText(
          line.slice(reviewCommentPrefix.length),
        )}</p>`;
      }

      return `<p>${formatHtmlText(line)}</p>`;
    }),
    "</div>",
  ].join("");
};
